import { datepickerToISOFormat } from '@yes.technology/react-toolkit'
import { DEFAULT_MIN_MAX_YEARS_FROM_TODAY } from '../modules/shared/Constants'

export type IsValidDateIntervalProps = {
  dateTime: string
  minDate?: string
  maxDate?: string
  lang: AllowedLocales
}

// TODO: import type from toolkit when available
type DatepickerToISOFormatOptions = Parameters<typeof datepickerToISOFormat>[1]

type ExtractLocale<T> = T extends { locale?: infer Locale } ? Locale : never

export type AllowedLocales = Extract<
  ExtractLocale<DatepickerToISOFormatOptions>,
  string
>

const TIME_REGEX: Record<AllowedLocales, RegExp> = {
  'de-DE': /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, // e.g.: 00:05:59
  'en-US': /^(1[012]|0?[1-9]):[0-5][0-9]:[0-5][0-9] [AP]M$/, // e.g.: 12:05:59 AM
  'es-419': /^(1[012]|0?[1-9]):[0-5][0-9]:[0-5][0-9] [ap]\.m\.$/, // e.g.: 12:05:59 a.m.
  'pt-BR': /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/ // e.g.: 00:05:59
}

/**
 * Validate only time portion according to locale formats supported by the
 * DatePicker. Returns true when time string matches the locale TIME_REGEX.
 */
export const isValidTime = (time: string, lang: AllowedLocales) => {
  return TIME_REGEX[lang].test(time)
}

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600

export const secondsFromTime = ({
  hour,
  minute,
  second
}: {
  hour: number
  minute: number
  second: number
}) => {
  return hour * SECONDS_PER_HOUR + minute * SECONDS_PER_MINUTE + second
}

/**
 * Parse a localized time string into seconds from midnight.
 * Returns number of seconds (0..86399) or null when parsing fails.
 */
export const timeToSeconds = (
  time: string,
  lang: AllowedLocales
): number | null => {
  if (!time || typeof time !== 'string') return null

  // en-US: 12-hour with AM/PM, e.g. 04:30:00 PM or 4:30:00 PM
  if (lang === 'en-US') {
    const m = time.match(/^(\d{1,2}):(\d{2}):(\d{2}) ([AP]M)$/)
    if (!m) return null
    let hour = parseInt(m[1], 10)
    const minute = parseInt(m[2], 10)
    const second = parseInt(m[3], 10)
    const meridiem = m[4]

    if (meridiem === 'PM' && hour !== 12) hour += 12
    if (meridiem === 'AM' && hour === 12) hour = 0

    return secondsFromTime({ hour, minute, second })
  }

  // es-419: 12-hour with a.m./p.m., e.g. 04:30:00 a.m. or 4:30:00 p.m.
  if (lang === 'es-419') {
    const m = time.match(/^(\d{1,2}):(\d{2}):(\d{2}) ([ap])\.m\.$/i)
    if (!m) return null
    let hour = parseInt(m[1], 10)
    const minute = parseInt(m[2], 10)
    const second = parseInt(m[3], 10)
    const ampm = m[4].toLowerCase()

    if (ampm === 'p' && hour !== 12) hour += 12
    if (ampm === 'a' && hour === 12) hour = 0

    return secondsFromTime({ hour, minute, second })
  }

  // pt-BR and de-DE: 24-hour format HH:mm:ss
  const m = time.match(/^(\d{1,2}):(\d{2}):(\d{2})$/)
  if (!m) return null
  const hour = parseInt(m[1], 10)
  const minute = parseInt(m[2], 10)
  const second = parseInt(m[3], 10)

  return secondsFromTime({ hour, minute, second })
}

const DATE_REGEX: Record<AllowedLocales, RegExp> = {
  'de-DE': /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/, // e.g.: 29.01.2025
  'en-US': /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/, // e.g.: 01/29/2025
  'es-419': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/, // 29/01/2025
  'pt-BR': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/ // e.g.: 29/01/2025
}

type IsValidDateTimeProps = {
  dateTime: string
  minYear?: string
  maxYear?: string
  lang: AllowedLocales
  contentType?: string
}

/**
 *  date format:      'dd/mm/YYYY' (according to locale)
 *  time format:      'hh:mm:ss' (according to locale)
 *  dateTime formant: '<date> | <time>'
 *
 *  contentType:      'date' or 'datetime'
 *  lang:             'en-US', 'pt-BR' etc.
 */
export const isValidDateTime = ({
  dateTime,
  lang,
  contentType = 'date'
}: IsValidDateTimeProps) => {
  const isValidDate = (dateOnly: string): boolean => {
    if (!DATE_REGEX[lang].test(dateOnly)) {
      return false
    }

    let day: number, month: number, year: number

    switch (lang) {
      case 'en-US': {
        const [m, d, y] = dateOnly.split('/')
        day = parseInt(d, 10)
        month = parseInt(m, 10)
        year = parseInt(y, 10)
        break
      }
      case 'de-DE': {
        const [d, m, y] = dateOnly.split('.')
        day = parseInt(d, 10)
        month = parseInt(m, 10)
        year = parseInt(y, 10)
        break
      }
      default: {
        // pt-BR, es-419 → dd/mm/yyyy
        const [d, m, y] = dateOnly.split('/')
        day = parseInt(d, 10)
        month = parseInt(m, 10)
        year = parseInt(y, 10)
      }
    }

    const date = new Date(year, month - 1, day)

    // valida se a data realmente existe
    const isRealDate =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day

    if (!isRealDate) return false

    // valida range de ano
    const yearNow = new Date().getFullYear()
    const isYearInsideRange =
      year >= yearNow - DEFAULT_MIN_MAX_YEARS_FROM_TODAY &&
      year <= yearNow + DEFAULT_MIN_MAX_YEARS_FROM_TODAY

    return isYearInsideRange
  }

  const isValidTime = (time: string) => TIME_REGEX[lang].test(time)

  if (contentType === 'date') {
    return isValidDate(dateTime)
  }

  const [date, time] = dateTime.split(' | ')
  return isValidDate(date) && !!time && isValidTime(time)
}

/**
 *  date format:      'dd/mm/aaaa' or 'dd-mm-aaaa'
 *  date format US:   'mm/dd/yyyy' or 'mm-dd-aaaa'
 *  time format:      'hh:mm:ss'
 *  dateTime formant: '<date> | <time>' (aka DatePickerFormat)
 *
 *  (min|max)Date: ISO format Eg. YYYY-MM-DDThh:mm:ss or YYYY-MM-DD
 *
 *  contentType:      'date' or 'datetime'
 *  lang:             'en-US' or 'pt-BR' etc.
 */
export const isValidDateInterval = ({
  dateTime,
  minDate,
  maxDate,
  lang
}: IsValidDateIntervalProps): boolean => {
  const isoFormattedDate = datepickerToISOFormat(dateTime, { locale: lang })
  const date = new Date(isoFormattedDate || '')

  const min = minDate ? new Date(minDate) : null
  const max = maxDate ? new Date(maxDate) : null

  const isAfterMinDate = min ? date >= min : true
  const isBeforeMaxDate = max ? date <= max : true

  return isAfterMinDate && isBeforeMaxDate
}

/**
 * Validates if a string is a valid UTC date or datetime (with trailing 'Z').
 * Accepts:
 *  - YYYY-MM-DD
 *  - YYYY-MM-DDTHH:mmZ
 *  - YYYY-MM-DDTHH:mm:ssZ
 *  - YYYY-MM-DDTHH:mm:ss.SSSZ
 */
export function isValidUTCDateString(input: string): boolean {
  const dateOnlyRe = /^(\d{4})-(\d{2})-(\d{2})$/
  const dateTimeUTCRe =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?Z$/

  const toInt = (s: string | undefined, fallback = 0) =>
    s != null ? Number.parseInt(s, 10) : fallback

  // YYYY-MM-DD
  const mDate = input.match(dateOnlyRe)
  if (mDate) {
    const year = toInt(mDate[1])
    const month = toInt(mDate[2])
    const day = toInt(mDate[3])

    if (month < 1 || month > 12) return false
    if (day < 1 || day > 31) return false

    const d = new Date(Date.UTC(year, month - 1, day))
    return (
      d.getUTCFullYear() === year &&
      d.getUTCMonth() === month - 1 &&
      d.getUTCDate() === day
    )
  }

  // YYYY-MM-DDTHH:mm[:ss[.SSS]]Z
  const mDateTime = input.match(dateTimeUTCRe)
  if (mDateTime) {
    const year = toInt(mDateTime[1])
    const month = toInt(mDateTime[2])
    const day = toInt(mDateTime[3])
    const hour = toInt(mDateTime[4])
    const minute = toInt(mDateTime[5])
    const second = toInt(mDateTime[6])
    const msRaw = mDateTime[7]
    const millisecond =
      msRaw == null ? 0 : Number.parseInt(msRaw.padEnd(3, '0').slice(0, 3), 10)

    if (month < 1 || month > 12) return false
    if (day < 1 || day > 31) return false
    if (hour < 0 || hour > 23) return false
    if (minute < 0 || minute > 59) return false
    if (second < 0 || second > 59) return false
    if (millisecond < 0 || millisecond > 999) return false

    const d = new Date(
      Date.UTC(year, month - 1, day, hour, minute, second, millisecond)
    )
    return (
      d.getUTCFullYear() === year &&
      d.getUTCMonth() === month - 1 &&
      d.getUTCDate() === day &&
      d.getUTCHours() === hour &&
      d.getUTCMinutes() === minute &&
      d.getUTCSeconds() === second &&
      d.getUTCMilliseconds() === millisecond
    )
  }

  return false
}

export const todayAtEndOfDay = () => {
  const today = new Date()
  today.setUTCHours(23, 59, 59, 999)
  return today.toISOString()
}

export const todayAtBeginningOfDay = () => {
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  return today.toISOString()
}
